/**
 * Country-specific utility functions
 * Handles currency formatting and other country-specific operations
 */

import store from "@/store";

/**
 * Get the current building from the store
 * @returns {object} Building object
 */
function getActiveBuilding() {
  const activeBuilding = store.state.settings.activeBuilding;
  return store.state.user.building.find(
    (el) => el.dbName === activeBuilding
  );
}

/**
 * Get the current building's currency from the store
 * @returns {string} Currency code (TND or KES), defaults to TND
 */
export function getBuildingCurrency() {
  return getActiveBuilding()?.currency || "TND";
}

/**
 * Get the current building's country code
 * @returns {string} Country code (TN or KE), defaults to TN
 */
export function getBuildingCountryCode() {
  return getActiveBuilding()?.countryCode || "TN";
}

/**
 * Check if the current building is in Kenya
 * @returns {boolean} True if building is in Kenya
 */
export function isKenyanSchool() {
  return getBuildingCountryCode() === "KE";
}

export function isEnglishLanguage() {
  const locale = store.state.settings?.locale || localStorage.getItem("lang") || "fr";
  return locale === "en" || locale === "en-US";
}

export function isKenyanEnglishSchool() {
  return isKenyanSchool() && isEnglishLanguage();
}

/**
 * Format a number as currency based on currency code
 * @param {number|string} amount - Amount to format
 * @param {string} currencyCode - Optional currency code (TND or KES). If not provided, will call getBuildingCurrency()
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currencyCode) {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  const currency = currencyCode || getBuildingCurrency();
  const locale = currency === "KES" ? "en-KE" : "fr-FR";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
}

/**
 * Get the label for a reduction type
 * @param {string} reductionType - The reduction type ("pourcentage", "amount")
 * @returns {string} The label to display ("%", "TND", or "KES")
 */
export function getReductionTypeLabel(reductionType) {
  if (reductionType === "amount") {
    const currency = getBuildingCurrency();
    return currency;
  }
  return "%";
}

export default {
  formatCurrency,
  getReductionTypeLabel,
  getBuildingCurrency,
  getBuildingCountryCode,
  isKenyanSchool,
  isEnglishLanguage,
  isKenyanEnglishSchool,
};
